import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { fetchConToken } from 'helpers/fetch';
import { useUIUtils } from 'hooks/useUIUtils';

function mensajeError(response) {
    if (!response || typeof response !== 'object') return null;
    if (response.errors) {
        const first = Object.values(response.errors)[0];
        return Array.isArray(first) ? first[0] : String(first);
    }
    const mensajes = response.mensajes || response.MENSAJES;
    const codigo = response.codigo ?? response.CODIGO;
    if (mensajes && codigo && Number(codigo) !== 200) return mensajes[0];
    return null;
}

function concedido(valor) {
    return String(valor || '').toUpperCase() === 'S';
}

function RolesPermisos() {
    const { changeGlobalTitle } = useUIUtils();
    const [roles, setRoles] = useState([]);
    const [rolId, setRolId] = useState('');
    const [permisos, setPermisos] = useState([]);
    const [asignados, setAsignados] = useState({});
    const [marcados, setMarcados] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        changeGlobalTitle('Asignar permisos', 'Seleccione un rol y marque los permisos que puede usar.');
        fetchConToken('Roles/GetList?pagina=0&totalXpagina=200').then((response) => {
            const error = mensajeError(response);
            if (error) {
                Swal.fire('Error', error, 'error');
                return;
            }
            setRoles(Array.isArray(response) ? response : []);
        });
        fetchConToken('Permisos/List').then((response) => {
            const error = mensajeError(response);
            if (error) {
                Swal.fire('Error', error, 'error');
                return;
            }
            const lista = Array.isArray(response) ? response : [];
            lista.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0) || (a.nombre || '').localeCompare(b.nombre || ''));
            setPermisos(lista);
        });
    }, [changeGlobalTitle]);

    useEffect(() => {
        if (!rolId) {
            setAsignados({});
            setMarcados({});
            return;
        }
        fetchConToken(`RolesPermisos/XRol?rol_id=${rolId}`).then((response) => {
            const error = mensajeError(response);
            if (error) {
                Swal.fire('Error', error, 'error');
                return;
            }
            const mapa = {};
            const checks = {};
            (Array.isArray(response) ? response : []).forEach((item) => {
                mapa[item.permiso_Id] = item;
                checks[item.permiso_Id] = concedido(item.valor);
            });
            setAsignados(mapa);
            setMarcados(checks);
        });
    }, [rolId]);

    const grupos = useMemo(() => {
        const texto = busqueda.trim().toLowerCase();
        const visibles = permisos.filter((item) => {
            if (!texto) return true;
            return [item.nombre, item.clave, item.tipo].some((valor) => (valor || '').toLowerCase().includes(texto));
        });
        const mapa = new Map();
        visibles.forEach((item) => {
            const tipo = item.tipo || 'Sin tipo';
            if (!mapa.has(tipo)) mapa.set(tipo, []);
            mapa.get(tipo).push(item);
        });
        return [...mapa.entries()];
    }, [permisos, busqueda]);

    function marcar(permisoId, valor) {
        setMarcados((prev) => ({ ...prev, [permisoId]: valor }));
    }

    function marcarGrupo(items, valor) {
        setMarcados((prev) => {
            const next = { ...prev };
            items.forEach((item) => { next[item.id] = valor; });
            return next;
        });
    }

    function guardar() {
        if (!rolId) {
            Swal.fire('Info', 'Seleccione un rol', 'info');
            return;
        }
        const cambios = permisos.filter((item) => {
            const estaba = concedido(asignados[item.id]?.valor);
            const queda = !!marcados[item.id];
            return estaba !== queda;
        });
        if (cambios.length === 0) {
            Swal.fire('Info', 'No hay cambios por guardar', 'info');
            return;
        }
        setGuardando(true);
        Promise.all(cambios.map((item) => fetchConToken('RolesPermisos', {
            id: asignados[item.id]?.id || 0,
            rol_Id: Number(rolId),
            permiso_Id: item.id,
            valor: marcados[item.id] ? 'S' : 'N'
        }, asignados[item.id]?.id ? 'PUT' : 'POST'))).then((respuestas) => {
            const error = respuestas.map(mensajeError).find(Boolean);
            if (error) {
                Swal.fire('Error', error, 'error');
                return;
            }
            return fetchConToken(`RolesPermisos/XRol?rol_id=${rolId}`).then((response) => {
                const errorCarga = mensajeError(response);
                if (errorCarga) {
                    Swal.fire('Error', errorCarga, 'error');
                    return;
                }
                const mapa = {};
                const checks = {};
                (Array.isArray(response) ? response : []).forEach((item) => {
                    mapa[item.permiso_Id] = item;
                    checks[item.permiso_Id] = concedido(item.valor);
                });
                setAsignados(mapa);
                setMarcados(checks);
                Swal.fire('Exito', 'Permisos del rol actualizados', 'success');
            });
        }).catch(() => {
            Swal.fire('Error', 'No se pudieron guardar los permisos', 'error');
        }).finally(() => setGuardando(false));
    }

    return (
        <div className='container-fluid'>
            <div className='d-flex flex-wrap gap-2 mb-3 align-items-end'>
                <div style={{ minWidth: 260 }}>
                    <label className='form-label' htmlFor='rol'>Rol</label>
                    <select id='rol' className='form-select' value={rolId} onChange={(event) => setRolId(event.target.value)}>
                        <option value=''>Seleccione un rol</option>
                        {roles.map((rol) => <option key={rol.id} value={rol.id}>{rol.nombre}</option>)}
                    </select>
                </div>
                <div style={{ minWidth: 260 }}>
                    <label className='form-label' htmlFor='buscar-permiso'>Buscar</label>
                    <input id='buscar-permiso' className='form-control' placeholder='Nombre, clave o tipo' value={busqueda} onChange={(event) => setBusqueda(event.target.value)} />
                </div>
                <button type='button' className='btn btn-outline-success' disabled={guardando || !rolId} onClick={guardar}>
                    {guardando ? 'Guardando...' : 'Guardar'}
                </button>
            </div>
            {!rolId && <p className='text-muted'>Seleccione un rol para ver y asignar sus permisos.</p>}
            {rolId && grupos.map(([tipo, items]) => {
                const activos = items.filter((item) => marcados[item.id]).length;
                return (
                    <div className='card mb-3' key={tipo}>
                        <div className='card-header d-flex flex-wrap justify-content-between align-items-center gap-2'>
                            <strong>{tipo}</strong>
                            <span className='text-muted'>{activos} de {items.length}</span>
                            <span>
                                <button type='button' className='btn btn-sm btn-outline-primary me-1' onClick={() => marcarGrupo(items, true)}>Marcar</button>
                                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => marcarGrupo(items, false)}>Quitar</button>
                            </span>
                        </div>
                        <div className='card-body'>
                            <div className='row'>
                                {items.map((item) => (
                                    <div className='col-md-6 col-lg-4 mb-2' key={item.id}>
                                        <div className='form-check'>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id={`permiso-${item.id}`}
                                                checked={!!marcados[item.id]}
                                                onChange={(event) => marcar(item.id, event.target.checked)}
                                            />
                                            <label className='form-check-label' htmlFor={`permiso-${item.id}`}>
                                                {item.nombre}
                                                <div className='text-muted small'>{item.clave}</div>
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export { RolesPermisos };
